import { useLocation } from 'react-router-dom';

import useGoBack from '@/hooks/useGoBack';
import useTranslations from '@/i18n/useTranslations';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import SuarteName from '@/shared-components/icons/components/public/SuarteName';
import Heading from '@/shared-components/text/components/Heading';
import Text from '@/shared-components/text/components/Text';

import LaunchOfferAnimation from './components/LaunchOfferAnimation';
import RoyaltiesRainAnimation from './components/RoyaltiesRainAnimation';

import './index.css';

// Content follows Sell In-store legal and product brief; update alongside policy changes.
export default function SellInStoreTermsPage() {
  const t = useTranslations('common.sell_in_store_terms');
  const location = useLocation();
  const fallbackRoute = location.state?.fromGalleryHelpSellAndShip
    ? '/profile/gallery-help/sell-and-ship'
    : '/';
  const goBackHandler = useGoBack(fallbackRoute);

  return (
    <div className="sell-in-store-terms__page">
      <div className="sell-in-store-terms__header">
        <BackArrowIcon className="sell-in-store-terms__back-button" onClick={goBackHandler} />
        <SuarteName className="sell-in-store-terms__brand" />
      </div>
      <nav className="sell-in-store-terms__subnav" aria-label={t('navigation.aria_label')}>
        <a href="#overview" className="sell-in-store-terms__subnav-link">{t('navigation.overview')}</a>
        <a href="#how-it-works" className="sell-in-store-terms__subnav-link">{t('navigation.how_it_works')}</a>
        <a href="#benefits" className="sell-in-store-terms__subnav-link">{t('navigation.benefits')}</a>
        <a href="#pricing" className="sell-in-store-terms__subnav-link">{t('navigation.pricing')}</a>
        <a href="#operations" className="sell-in-store-terms__subnav-link">{t('navigation.operations')}</a>
        <a href="#security" className="sell-in-store-terms__subnav-link">{t('navigation.security')}</a>
        <a href="#faq" className="sell-in-store-terms__subnav-link">{t('navigation.faq')}</a>
      </nav>
      <header id="overview" className="sell-in-store-terms__hero">
        <div>
          <Heading className="sell-in-store-terms__title">
            {t('hero.title')}
          </Heading>
          <Text className="sell-in-store-terms__subtitle" paragraph>
            {t('hero.subtitle')}
          </Text>
        </div>
      </header>
      <section className="sell-in-store-terms__banner">
        <div className="sell-in-store-terms__banner-content">
          <Heading className="sell-in-store-terms__section-title">
            {t('commission.title')}
          </Heading>
          <Text className="sell-in-store-terms__banner-copy" paragraph>
            {t('commission.body_before')} <strong>{t('commission.emphasis')}</strong>{t('commission.body_after')}
            <br /><br />
            {t('commission.body_secondary')}
          </Text>
        </div>
        <LaunchOfferAnimation className="sell-in-store-terms__banner-visual" />
      </section>
      <section id="how-it-works" className="sell-in-store-terms__card">
        <Heading className="sell-in-store-terms__section-title">
          {t('how_it_works.title')}
        </Heading>
        <ol className="sell-in-store-terms__list ordered">
          <li>
            <strong>{t('how_it_works.mark_sold.title')}</strong>
            {t('how_it_works.mark_sold.body')}
            <ul className="sell-in-store-terms__sub-list">
              <li>{t('how_it_works.mark_sold.existing_collector')}</li>
              <li>{t('how_it_works.mark_sold.invite_collector')}</li>
            </ul>
          </li>
          <li>
            <strong>{t('how_it_works.confirm_sale.title')}</strong>
            {t('how_it_works.confirm_sale.body')}
          </li>
          <li>
            <strong>{t('how_it_works.collector_claim.title')}</strong>
            {t('how_it_works.collector_claim.body')}
          </li>
          <li>
            <strong>{t('how_it_works.certificate.title')}</strong>
            {t('how_it_works.certificate.body')}
            <ul className="sell-in-store-terms__sub-list">
              <li>{t('how_it_works.certificate.existing_account')}</li>
              <li>{t('how_it_works.certificate.invited_collector')}</li>
            </ul>
            <br />
            {t('how_it_works.certificate.secondary_body')}
          </li>
          <li>
            <strong>{t('how_it_works.eligibility.title')}</strong>
            {t('how_it_works.eligibility.body')}
          </li>
        </ol>
        <div className="sell-in-store-terms__alert" role="note">
          <span className="sell-in-store-terms__alert-dot" aria-hidden="true" />
          <span><strong>{t('how_it_works.tip.title')}</strong> {t('how_it_works.tip.body')}</span>
        </div>
      </section>
      <section id="benefits" className="sell-in-store-terms__grid">
        <article className="sell-in-store-terms__card">
          <Heading className="sell-in-store-terms__section-title">
            {t('benefits.galleries.title')}
          </Heading>
          <ul className="sell-in-store-terms__list">
            {['provenance', 'visibility', 'commission_ladder', 'relationships', 'protected_records', 'royalties', 'free_documentation'].map((key) => <li key={key}><strong>{t(`benefits.galleries.${key}.title`)}</strong> {t(`benefits.galleries.${key}.body`)}</li>)}
          </ul>
        </article>

        <article className="sell-in-store-terms__card">
          <Heading className="sell-in-store-terms__section-title">
            {t('benefits.collectors.title')}
          </Heading>
          <ul className="sell-in-store-terms__list">
            {['ownership', 'collection', 'records', 'secondary_market', 'privacy', 'secure_claiming', 'resale_certificates'].map((key) => <li key={key}><strong>{t(`benefits.collectors.${key}.title`)}</strong> {t(`benefits.collectors.${key}.body`)}</li>)}
          </ul>
        </article>
      </section>
      <section id="pricing" className="sell-in-store-terms__card">
        <Heading className="sell-in-store-terms__section-title">
          {t('pricing.title')}
        </Heading>
        <div className="sell-in-store-terms__table-wrapper">
          <table className="sell-in-store-terms__table">
            <thead>
              <tr>
                <th>{t('pricing.headers.scenario')}</th>
                <th>{t('pricing.headers.commission')}</th>
                <th>{t('pricing.headers.who_pays')}</th>
                <th>{t('pricing.headers.notes')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('pricing.in_store.scenario')}</td>
                <td>0%</td>
                <td>{t('pricing.in_store.who_pays')}</td>
                <td>{t('pricing.in_store.notes')}</td>
              </tr>
              <tr>
                <td>{t('pricing.resale.scenario')}</td>
                <td>{t('pricing.resale.commission')}</td>
                <td>{t('pricing.resale.who_pays')}</td>
                <td>{t('pricing.resale.notes')}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      <section className="sell-in-store-terms__card">
        <Heading className="sell-in-store-terms__section-title">
          {t('royalties.title')}
        </Heading>
        <ul className="sell-in-store-terms__list">
          <li>{t('royalties.items.checkout')}</li>
          <li>{t('royalties.items.records')}</li>
          <li>{t('royalties.items.eligibility')}</li>
        </ul>
        <RoyaltiesRainAnimation className="sell-in-store-terms__royalties-visual" />
      </section>
      <section id="operations" className="sell-in-store-terms__grid">
        <article className="sell-in-store-terms__card">
          <Heading className="sell-in-store-terms__section-title">
            {t('operations.invoicing.title')}
          </Heading>
          <ul className="sell-in-store-terms__list">
            <li>{t('operations.invoicing.recording')}</li>
            <li>
              {t('operations.invoicing.options_intro')}
              <ul className="sell-in-store-terms__sub-list">
                <li>{t('operations.invoicing.manual')}</li>
                <li>{t('operations.invoicing.auto_generate')}</li>
              </ul>
            </li>
          </ul>
        </article>

        <article className="sell-in-store-terms__card">
          <Heading className="sell-in-store-terms__section-title">
            {t('operations.transparency.title')}
          </Heading>
          <ul className="sell-in-store-terms__list">
            <li>{t('operations.transparency.sale_record')}</li>
            <li>{t('operations.transparency.certificate')}</li>
            <li>{t('operations.transparency.audit')}</li>
          </ul>
        </article>
      </section>
      <section id="security" className="sell-in-store-terms__grid">
        <article className="sell-in-store-terms__card">
          <Heading className="sell-in-store-terms__section-title">
            {t('security.title')}
          </Heading>
          <ul className="sell-in-store-terms__list">
            <li>{t('security.items.verified_galleries')}</li>
            <li>{t('security.items.invitations')}</li>
            <li>{t('security.items.records')}</li>
          </ul>
        </article>

        <article className="sell-in-store-terms__card">
          <Heading className="sell-in-store-terms__section-title">
            {t('responsibilities.title')}
          </Heading>
          <ul className="sell-in-store-terms__list">
            <li>{t('responsibilities.authenticity')}</li>
            <li>{t('responsibilities.accurate_price')}</li>
          </ul>
        </article>
      </section>
      <section id="faq" className="sell-in-store-terms__card">
        <Heading className="sell-in-store-terms__section-title">
          {t('faq.title')}
        </Heading>
        <ul className="sell-in-store-terms__list sell-in-store-terms__list--faq">
          <li>
            <strong>{t('faq.claim_benefits.question')}</strong>
            <span>{t('faq.claim_benefits.answer')}</span>
          </li>
          <li>
            <strong>{t('faq.artist_benefits.question')}</strong>
            <span>{t('faq.artist_benefits.answer')}</span>
          </li>
          <li>
            <strong>{t('faq.edit_price.question')}</strong>
            <span>{t('faq.edit_price.answer_before_contact')}{' '}<a href="mailto:galleries@suarte.art">galleries@suarte.art</a>{t('faq.edit_price.answer_after_contact')}</span>
          </li>
          <li>
            <strong>{t('faq.commission_ladder.question')}</strong>
            <span>{t('faq.commission_ladder.answer')}</span>
          </li>
          <li>
            <strong>{t('faq.resell.question')}</strong>
            <span>{t('faq.resell.answer')}</span>
          </li>
          <li>
            <strong>{t('faq.upload_sold.question')}</strong>
            <span>{t('faq.upload_sold.answer')}</span>
          </li>
          <li>
            <strong>{t('faq.proof_of_sale.question')}</strong>
            <span>{t('faq.proof_of_sale.answer')}</span>
          </li>
          <li>
            <strong>{t('faq.no_claim.question')}</strong>
            <span>{t('faq.no_claim.answer')}</span>
          </li>
        </ul>
      </section>
    </div>
  );
}
